"use client";

import { differenceInMinutes, differenceInHours, differenceInDays, format } from "date-fns";
import { enUS } from "date-fns/locale";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Typography } from "@/components/shared/Typography";
import { ArticleListType } from "@/types/article";
import { getArticleImageUrl, getMediaSrcSet } from "@/utils/getImageUrl";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import CustomImage from "../shared/CustomImage";
import TitleTag from "../shared/TitleTag";

import { formatDate } from "@/utils/dateUtils";

interface VerticalArticleCardProps {
  variant?: "md" | "lg";
  article: ArticleListType;
  showSummary?: boolean;
  showDate?: boolean;
  showCategory?: boolean;
  /** Use "contain" for featured main images so the full photo is visible. */
  imageFit?: "cover" | "contain";
}

// const colors = ["#FF6E0D", "#007DFF", "#EB8D02", "#FF5575"];

export default function VerticalArticleCard({
  variant = "md",
  article,
  showSummary = false,
  showDate = false,
  showCategory = false,
  imageFit = "cover",
}: VerticalArticleCardProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = (locale?.split("-")[0] || "en") as AppLocale;

  if (!article) return null;

  const {
    title,
    slug,
    image,
    summary,
    categoryId,
    subCategoryId,
    isLiveNews,
    publishedAt,
    isInPrint,
  } = article;

  const formatTimeAgo = () => {
    if (!publishedAt) return "";
    const now = new Date();
    const published = new Date(publishedAt);
    const diffMinutes = differenceInMinutes(now, published);
    const diffHours = differenceInHours(now, published);
    const diffDays = differenceInDays(now, published);

    if (language === "te") {
      if (diffMinutes < 60) {
        return `${diffMinutes} ${diffMinutes === 1 ? "నిమిషం" : "నిమిషాల"} క్రితం`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "గంట" : "గంటల"} క్రితం`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? "రోజు" : "రోజుల"} క్రితం`;
      } else {
        return formatDate(published,language);
      }
    } else {
      if (diffMinutes < 60) {
        return `${diffMinutes} Min Ago`;
      } else if (diffHours < 24) {
        return `${diffHours} Hour${diffHours > 1 ? "s" : ""} Ago`;
      } else if (diffDays < 7) {
        return `${diffDays} Day${diffDays > 1 ? "s" : ""} Ago`;
      } else {
        return formatDate(published, language);
      }
    }
  };

  return (
    <div className="group">
      <Link
        href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
        aria-label={`View news: ${title}`}
      >
        <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl bg-[#f0f0f0] dark:bg-[#2a2a2a]">
          <CustomImage
            src={getArticleImageUrl(image, variant === "lg" ? "sd" : "ld")}
            srcSet={getMediaSrcSet(image, variant === "lg" ? ["ld", "sd", "md"] : ["ld", "sd"])}
            sizes={variant === "lg" ? "(max-width: 768px) 100vw, 640px" : "(max-width: 768px) 50vw, 360px"}
            className={`h-full w-full img-zoom-hover ${
              imageFit === "contain" ? "object-contain" : "object-cover"
            } object-${article.focusPosition || "center"}`}
            alt={image?.alt || ""}
            phImg={
              variant === "lg"
                ? "/images/placeholder-grey-sd.png"
                : "/images/placeholder-grey-ld.png"
            }
          />

          {showCategory && (
            <div className="absolute top-0 left-6 bg-[#007DFF] text-white text-xs px-2 uppercase py-[3px] font-[600]">
              {getLocalizedString(categoryId?.name, locale) || categoryId?.slug}
            </div>
          )}
        </div>
      </Link>
      <div className={variant === "md" ? "mt-3" : "mt-4"}>
        <Link
          href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
          aria-label={`View news: ${title}`}
        >
          <Typography
            language={language}
            variant={variant === "md" ? "h6" : "h5"}
            className="line-clamp-3"
          >
            {isLiveNews &&
              publishedAt &&
              Date.now() - new Date(publishedAt).getTime() <= 12 * 60 * 60 * 1000 && (
                <TitleTag labelKey="LIVE_NEWS" />
              )}
            {isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
            {title}
          </Typography>
        </Link>

        {showSummary && (
          <Link
            href={getNewsDetailsUrl(categoryId, subCategoryId, slug, locale)}
            aria-live="polite"
            aria-label={`View news: ${title}`}
          >
            <Typography variant="body1" language={language} className="mt-3 line-clamp-4">
              {summary}
            </Typography>
          </Link>
        )}

        {showDate && (
          <span className="text-gray-500 dark:text-white mt-[4px] font-medium !text-[10px]">
            {formatTimeAgo()}
          </span>
        )}
      </div>
    </div>
  );
}
